import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Member, CheckInValidation } from '../../types';

// Extended member interface for check-in data that may have different field names
interface CheckInMember extends Partial<Member> {
  endDate?: string; // Server uses endDate instead of expiryDate
  daysRemaining?: number; // Server-calculated value
}

interface CheckInModalProps {
  open: boolean;
  onClose: () => void;
  member: CheckInMember;
  validation: CheckInValidation;
  timestamp: string;
}

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    case 'contract-expired':
    case 'payment-overdue':
      return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
    case 'suspended':
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    default:
      return null;
  }
};

const getStatusChipColor = (status: string) => {
  switch (status) {
    case 'active': return 'success';
    case 'inactive': return 'error';
    case 'suspended': return 'warning';
    case 'expired': return 'error';
    default: return 'default';
  }
};

const getPaymentChipColor = (status: string) => {
  switch (status) {
    case 'current': return 'success';
    case 'overdue': return 'error';
    case 'pending': return 'warning';
    default: return 'default';
  }
};

const CheckInModal: React.FC<CheckInModalProps> = ({ open, onClose, member, validation, timestamp }) => {
  // Auto-close timer for successful check-ins
  useEffect(() => {
    if (open && validation.status === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 10000); // 10 seconds
      return () => clearTimeout(timer);
    }
  }, [open, validation.status, onClose]);

  // Add extra attention when modal opens (for focus/visibility issues)
  useEffect(() => {
    if (open) {
      // Scroll to top to ensure modal is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Try to flash the document title for attention
      const originalTitle = document.title;
      if (!document.hidden) {
        document.title = `🔔 ${member.firstName} Checked In - ${originalTitle}`;
        setTimeout(() => {
          document.title = originalTitle;
        }, 5000);
      }
    }
  }, [open, member.firstName]);

  // Helper functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getDaysRemaining = (endDate: string) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  if (!open) return null;

  // Use server-calculated daysRemaining if available, otherwise calculate client-side
  const daysRemaining = member.daysRemaining !== undefined 
    ? member.daysRemaining 
    : member.endDate 
      ? getDaysRemaining(member.endDate) 
      : member.expiryDate 
        ? getDaysRemaining(member.expiryDate) 
        : 0;
  
  const memberAge = member.dateOfBirth ? calculateAge(member.dateOfBirth) : member.age || null;

  return (
    <Paper 
      elevation={6}
      sx={{ 
        position: 'fixed',
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%', 
        maxWidth: 400,
        zIndex: 1300,
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        border: 2,
        borderColor: validation.status === 'success' ? 'success.main' : 'error.main',
        boxShadow: validation.status === 'success' 
          ? '0 8px 24px rgba(76, 175, 80, 0.3)' 
          : '0 8px 24px rgba(244, 67, 54, 0.3)',
        animation: 'slideInFromTop 0.4s ease-out',
        '@keyframes slideInFromTop': {
          '0%': {
            transform: 'translateX(-50%) translateY(-100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateX(-50%) translateY(0)',
            opacity: 1,
          },
        },
      }}
    >


      <Box sx={{ p: 3, textAlign: 'center' }}>
        {/* Member Avatar */}
        <Avatar
          src={member.profileImage || undefined}
          alt={member.fullName}
          sx={{ 
            width: 80, 
            height: 80, 
            mx: 'auto', 
            mb: 2,
            fontSize: '1.5rem',
            bgcolor: 'grey.400'
          }}
        >
          {!member.profileImage && (
            `${member.firstName?.charAt(0) || ''}${member.lastName?.charAt(0) || ''}`
          )}
        </Avatar>
        
        {/* Member Name */}
        <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
          {member.fullName || `${member.firstName} ${member.lastName}`}
        </Typography>
        
        {/* Member ID */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Member ID: {member.memberId}
        </Typography>
        
        {/* Status Chips */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
          <Chip 
            label={member.membershipStatus || 'active'}
            size="small"
            color={getStatusChipColor(member.membershipStatus || 'active')}
            sx={{ textTransform: 'capitalize' }}
          />
          <Chip 
            label={member.paymentStatus || 'current'}
            size="small"
            color={getPaymentChipColor(member.paymentStatus || 'current')}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
      </Box>
      
      {/* Member Details */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Membership Type
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
              {member.membershipType || 'Tenant'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Joined
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {member.startDate ? formatDate(member.startDate) : 'N/A'}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Expires
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {member.endDate ? formatDate(member.endDate) : member.expiryDate ? formatDate(member.expiryDate) : 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Days Remaining
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500,
                color: daysRemaining < 0 ? 'error.main' : daysRemaining <= 30 ? 'warning.main' : 'success.main'
              }}
            >
              {daysRemaining < 0 ? 'Expired' : `${daysRemaining} days`}
            </Typography>
          </Box>
        </Box>
        
        {memberAge && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Age
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {memberAge} years
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* Access Status Badge */}
        <Box sx={{ 
          mt: 2, 
          textAlign: 'center'
        }}>
          <Chip
            icon={<StatusIcon status={validation.status} />}
            label={validation.status === 'success' ? 'Access Granted' : 'Access Denied'}
            color={validation.status === 'success' ? 'success' : 'error'}
            variant="filled"
            sx={{ 
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              py: 2,
              px: 3
            }}
          />
        </Box>
        
        {/* Dismiss Button for non-successful check-ins */}
        {validation.status !== 'success' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onClose}
              sx={{ minWidth: 100 }}
            >
              Dismiss
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CheckInModal;
